/* eslint-disable no-nested-ternary */
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import { useIntl } from 'react-intl';
import { easeInOutSine, hoverTiming } from '../styles/animations';
import { ChevronRight, LocationIcon } from '../vectors/Misc';
import { usePageLocale } from '../../hooks/usePageLocale';
import { Navigator } from '../LanguageHelpers/Navigator';
import { useDefaultLocale } from '../../hooks/useDefaultLocale';
import { useProductsSlug } from '../../hooks/useProductsSlug';

// Common styles

const commonButtonStyles = css`
  margin: 0;
  appearance: none;
  height: min-content;
  border: none;
  background: none;
`;

// Hamburger

const HamburgerContent = styled.button`
  --height: 4px;
  --width: 40px;
  padding: 0;
  ${commonButtonStyles}
  width: 40px;
  height: 40px;
  margin-top: 4px;

  & span {
    display: block;
    margin-bottom: var(--height);
    background: var(--gold-2);
    height: var(--height);
    width: var(--width);
    transition: all ${hoverTiming} ${easeInOutSine};
  }

  @media screen and (min-width: 1051px) {
    display: none;
  }

  ${({ open }) =>
    open &&
    css`
      & span {
        &:nth-of-type(1) {
          transform: translateY(var(--height)) rotate(45deg);
        }
        &:nth-of-type(2) {
          display: none;
        }
        &:nth-of-type(3) {
          transform: translateY(calc(var(--height) * -1)) rotate(-45deg);
        }
      }
    `}
`;

export const Hamburger = ({ onClick, open }) => {
  const intl = useIntl();
  return (
    <HamburgerContent
      open={open}
      onClick={onClick}
      aria-label={intl.formatMessage({ id: 'openMenu' })}
    >
      <span />
      <span />
      <span />
    </HamburgerContent>
  );
};

// Icon button

const IconButtonBtnContainer = styled.button`
  ${commonButtonStyles}
  padding: var(--gap-6);
  cursor: pointer;
  display: flex;

  & svg path {
    transition: fill ${hoverTiming} ${easeInOutSine};
  }

  @media (hover: hover) {
    &:hover svg path {
      fill: var(--foreground-white);
    }
  }
`;

const IconButtonContainer = styled(Link)`
  ${commonButtonStyles}
  padding: var(--gap-6);
  cursor: pointer;

  & svg path {
    transition: fill ${hoverTiming} ${easeInOutSine};
  }

  @media (hover: hover) {
    &:hover svg path {
      fill: var(--foreground-white);
    }
  }
`;

const IconButtonContainerAnchor = styled.a`
  ${commonButtonStyles}
  padding: var(--gap-6);
  cursor: pointer;
  display: flex;

  & svg path {
    transition: fill ${hoverTiming} ${easeInOutSine};
  }

  @media (hover: hover) {
    &:hover svg path {
      fill: var(--foreground-white);
    }
  }
`;

export const IconButtonBtn = ({ icon, onClick, ariaLabel }) => (
  <IconButtonBtnContainer onClick={onClick} aria-label={ariaLabel}>
    {icon}
  </IconButtonBtnContainer>
);

export const IconButton = ({ icon, iconLink, ariaLabel }) => (
  <IconButtonContainer to={iconLink} aria-label={ariaLabel}>
    {icon}
  </IconButtonContainer>
);

export const IconButtonAnchor = ({ icon, iconLink, ariaLabel, className }) => (
  <IconButtonContainerAnchor
    className={className}
    href={iconLink}
    rel="noreferrer"
    aria-label={ariaLabel}
  >
    {icon}
  </IconButtonContainerAnchor>
);

// Button

export const ButtonUniversal = styled.button`
  font-size: var(--font-size);
  padding: 0.4em 1em;
  font-weight: ${({ $bolder }) => ($bolder ? '700' : '400')};
  border-radius: 5px;
  color: var(--foreground-white);
  font-weight: 500;
  width: min-content;
  border: none;
  height: min-content;
  white-space: nowrap;
  transition: 200ms background ${easeInOutSine};
  background: ${({ $buttonGold }) =>
    $buttonGold ? 'var(--gold-2)' : 'var(--blue-2)'};

  @media (hover: hover) {
    &:hover {
      background: ${({ $buttonGold }) =>
        $buttonGold ? 'var(--gold-1)' : 'rgb(115, 139, 174)'};
    }
  }

  @media screen and (max-width: 810px) {
    font-size: var(--font-size-2);
  }
`;

const buttonNormalShared = css`
  padding: 0.4em 1em;
  font-weight: ${({ $bolder }) => ($bolder ? '700' : '400')};
  border-radius: 5px;
  color: var(--foreground-white);
  width: min-content;
  white-space: nowrap;
  transition: 200ms background ${easeInOutSine};
  margin-top: ${({ $hasTopMargin }) => $hasTopMargin && 'var(--gap-6)'};
  background: ${({ $buttonGold }) =>
    $buttonGold ? 'var(--gold-2)' : 'var(--blue-2)'};

  @media (hover: hover) {
    &:hover {
      background: ${({ $buttonGold }) =>
        $buttonGold ? 'var(--gold-1)' : 'var(--blue-1)'};
    }
  }

  @media screen and (max-width: 800px) {
    background: ${({ $hoverMobileColor }) => `${$hoverMobileColor} !important`};
  }
`;

const ButtonNormalShape = styled(Link)`
  ${buttonNormalShared};
`;

const NavigatorButtonNormalShape = styled(Navigator)`
  ${buttonNormalShared};
`;

export const ButtonNormal = ({
  buttonText,
  buttonLink,
  $buttonGold,
  $hasTopMargin,
  $hoverMobileColor,
  $bolder,
}) => (
  <ButtonNormalShape
    $bolder={$bolder}
    $hoverMobileColor={$hoverMobileColor}
    $hasTopMargin={$hasTopMargin}
    $buttonGold={$buttonGold}
    to={buttonLink}
  >
    {buttonText}
  </ButtonNormalShape>
);

export const NavigatorButtonNormal = ({
  buttonText,
  recordId,
  $buttonGold,
  $hasTopMargin,
  $hoverMobileColor,
  $bolder,
}) => (
  <NavigatorButtonNormalShape
    $bolder={$bolder}
    $hoverMobileColor={$hoverMobileColor}
    $hasTopMargin={$hasTopMargin}
    $buttonGold={$buttonGold}
    recordId={recordId}
  >
    {buttonText}
  </NavigatorButtonNormalShape>
);

// See all rounded button

const SeelAllChevronShape = styled(Link)`
  display: grid;
  flex-direction: column;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  justify-items: center;
  row-gap: var(--gap-6);

  @media (hover: hover) {
    &:hover {
      & > div > svg path {
        fill: var(--blue-2);
      }
      & > div {
        border-color: var(--blue-2);
      }
    }
  }
`;

const SeeAllChevronText = styled.div`
  font-size: var(--font-size-3);
  color: var(--blue-2);
  white-space: nowrap;
  font-weight: 700;
`;

const SeeAllSvgContainer = styled.div`
  border-radius: 200px;
  border: 2px solid grey;
  transition: border-color 200ms ${easeInOutSine};
  width: 80px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;

  & svg {
    margin-left: 10%;
    transform: scale(0.6);
    & path {
      transition: fill 200ms ${easeInOutSine};
      fill: grey;
    }
  }
`;

export const SeeAllChevron = () => {
  const { locale } = usePageLocale();
  const { defaultLocale } = useDefaultLocale();
  const { slug } = useProductsSlug(locale);
  const intl = useIntl();

  return (
    <SeelAllChevronShape to={locale === defaultLocale ? `/${slug}` : `/${locale}/${slug}`}>
      <SeeAllSvgContainer>
        <ChevronRight />
      </SeeAllSvgContainer>
      <SeeAllChevronText>
        {intl.formatMessage({ id: 'discoverAll' })}
      </SeeAllChevronText>
    </SeelAllChevronShape>
  );
};

// Button location

const ButtonLocationShape = styled.button`
  background: none;
  border-radius: 5px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.4em 0.2em;
  line-height: 1;
  color: ${({ blue }) => (blue ? 'var(--blue-2)' : 'var(--gold-2)')};
  font-weight: 500;
  border: ${({ blue }) =>
    blue ? '2px solid var(--blue-2)' : '2px solid var(--gold-2)'};

  @media screen and (min-width: 601px) {
    display: ${({ blue }) => blue && 'none'};
  }

  & svg {
    width: 18px;
    height: 18px;
    margin-right: var(--gap-7);

    & path {
      fill: var(--gold-2);
    }
  }
`;

export const ButtonLocation = ({ children, onClick }) => (
  <ButtonLocationShape onClick={onClick}>
    <LocationIcon />
    {children}
  </ButtonLocationShape>
);

export const ButtonMap = ({ children, onClick, blue }) => (
  <ButtonLocationShape blue={blue} onClick={onClick}>
    {children}
  </ButtonLocationShape>
);

// Refinement buttons

export const RefinementButton = styled.button`
  background: ${({ radiusMin, radiusMax, noRadius }) =>
    radiusMin === 5000
      ? 'var(--gold-2)'
      : radiusMax === 50000
      ? 'var(--gold-2)'
      : noRadius === 'all'
      ? 'var(--gold-2)'
      : 'var(--blue-2)'};
  color: white;
  border: none;
  padding: 0.25em 0.4em;
  border-radius: 5px;
  font-size: var(--font-size-micro-1);
  font-weight: 500;
`;

export const RefinementButtonsGrid = styled.div`
  display: grid;
  grid-auto-flow: column;
  column-gap: var(--gap-6);
`;

// Other

export const NullButton = styled.button`
  ${commonButtonStyles}
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;

  & svg {
    width: 20px;
    height: 20px;
  }
`;
